import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import { Box, Button, Grid, Typography } from "@mui/material";

import { TextFieldCustom } from "../helpers/formFields";
import { getIconFromDriverName } from "./iconFunctions";
import { tooltip } from '../helpers/tooltip';
import { formatNumberThsndAndDec } from "../helpers/stringFormat";

/**
 * Props:
 * - metrics: array of { name, label, unit, adornment: 'start'|'end', width: 'full'|'half' }
 * - suggestedJson: { [metricName]: { value, unit } } (from AI)
 * - done: boolean (enable editing once AI has suggested values)
 * - onSubmit: (payload, { setSubmitting, setErrors }) => void
 */
export default function ChatMetricsDriverForm({
    iconName,
    metrics = [],
    suggestedJson = {},
    done = false,
    onSubmit
}) {
    // Build Formik initial values from suggestedJson
    const initialValues = useMemo(() => {
        const vals = {};
        metrics.forEach(m => {
            const sug = suggestedJson?.[m.name];
            const base = (sug?.value ?? "0").toString();
            // Format ONLY when unit is not '%'
            vals[m.name] = m.unit !== "%" ? formatNumberThsndAndDec(base) : base;
        });
        return vals;
    }, [metrics, suggestedJson]);

    return (
        <Formik
            key={JSON.stringify({ suggestedJson, done })}
            initialValues={initialValues}
            enableReinitialize
            validate={(values) => {
                const errs = {};
                metrics.forEach(m => {
                    if (values[m.name] === undefined) return;
                });
                return errs;
            }}
            onSubmit={(values, helpers) => {
                // Normalize payload: { metricName: { value: number, unit } }
                const payload = {};
                metrics.forEach((m) => {
                    const raw = values[m.name] ?? "";
                    // Strip commas ONLY for non-% fields before parsing
                    const cleaned = m.unit !== "%" ? raw.replace(/,/g, "") : raw;
                    const parsed = cleaned === "" ? null : Number(cleaned);
                    payload[m.name] = {
                        value: parsed,
                        unit: m.unit ?? ""
                    };
                });
                onSubmit(payload, helpers);
            }}
        >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700 }}>
                    <Grid container spacing={2}>
                        {metrics.map((m) => {
                            const xs = m.width === "half" ? 6 : 12;
                            const startAdornment = m.adornment === "start" ? (m.unit || "") : undefined;
                            const endAdornment = m.adornment === "end" ? (
                                <Typography variant="caption">{m.unit}</Typography>
                            ) : undefined;

                            return (
                                <Grid item xs={xs} key={m.name} sx={{ display: 'flex' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', width: '100%', height: '100%' }}>
                                        <Typography variant="caption" gutterBottom>
                                            {m.label}{tooltip(m.description)}
                                        </Typography>
                                        <TextFieldCustom
                                            name={m.name}
                                            value={values[m.name] ?? ""}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched[m.name] && Boolean(errors[m.name])}
                                            helperText={touched[m.name] && errors[m.name]}
                                            startAdornment={startAdornment}
                                            endAdornment={endAdornment}
                                            fullWidth
                                            disabled={!done}
                                            greenBackground={m.compare}
                                        />
                                    </Box>
                                </Grid>
                            );
                        })}

                        {/* Submit button */}
                        <Grid item xs={12} sx={{ textAlign: "right" }}>
                            <Button
                                variant="contained"
                                startIcon={<Box component={getIconFromDriverName(iconName)} />}
                                disabled={isSubmitting || !done}
                                type="submit"
                                size="small"
                                sx={{ textTransform: "none" }}
                            >
                                {isSubmitting ? "Saving…" : "Add Driver"}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Formik>
    );
}

ChatMetricsDriverForm.propTypes = {
    metrics: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            unit: PropTypes.string,
            adornment: PropTypes.oneOf(["start", "end"]),
            width: PropTypes.oneOf(["full", "half"])
        })
    ).isRequired,
    suggestedJson: PropTypes.object,
    done: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired
};
